// EditorFlapPrompt.jsx — "this carton has no flaps yet. Add them?"
//
// WHY THIS EXISTS
// ---------------
// The Layout Editor's desk takes cartons straight from an upload or the
// library, and prepress has not run on any of them. Placing such an outline as
// drawn puts a carton with no glue flap, no tuck and no dust flap on the sheet:
// it nests against its neighbours by the wrong shape, and if it reaches a plate
// it is cut as a carton nobody can make up. Nothing downstream catches it —
// the die is a perfectly valid polygon, the nest is clean, the export succeeds.
// A silent wrong answer, which is the kind this screen exists to stop.
//
// So the desk asks, once, at the moment the carton arrives.
//
// WHAT IT IS NOT
// --------------
// Not a validator and not an automatic transform. `flapsMissing` reads a corner
// count against the deployment's published unflapped topologies, which rev 12
// §2 is explicit is an AFFORDANCE: a hand-drawn dieline that lands on an
// accepted count reads as unflapped, and a family this server publishes no
// topology for is not recognised at all. Both directions are wrong sometimes,
// so the operator decides and "Place as drawn" is always one click away.
//
// The flaps themselves come from PREPRESS (POST /planner/prepress/describe,
// `plain` mode, one carton, no sheet — sub-second and the same `apply_prepress`
// a nest runs). Nothing here computes geometry: a flap is a manufacturing
// decision with rules and configuration behind it, and a client that drew its
// own would be drawing a different die from the one the press cuts.

import React from 'react';
import { useTheme } from './theme';
import DiePreview from './DiePreview';
import { describePrepressError, blankHasFlaps, blankSummaryText } from './prepress';

/**
 * @param {Object|null} pending  the carton being asked about, or null when the
 *   queue is empty: { sourceId, corners, suggested, cartonType, remaining }
 * @param {(cartonType: string|null) => void} onAddFlaps
 * @param {() => void} onPlaceAsDrawn
 * @param {() => void} onCancel   drop this carton from the desk entirely
 */
export default function EditorFlapPrompt({
  pending, busy = false, error = null, described = null,
  cartonType, onCartonType, onAddFlaps, onPlaceAsDrawn, onCancel, caps,
}) {
  const { P, FN, MO, DISPLAY } = useTheme();
  if (!pending) return null;

  const { sourceId, corners, suggested = [], remaining = 0 } = pending;
  // Prepress cannot be asked without a family, and this deployment may take
  // several at this corner count — rev 12 §1.3: a family cannot be read off a
  // vertex count, so where there is a real choice the operator makes it.
  const needsChoice = suggested.length > 1 && !cartonType;
  const canAsk = suggested.length > 0 && !!cartonType;

  const btn = (tone) => ({
    fontFamily: MO, fontSize: 11.5, padding: '8px 15px', borderRadius: 6,
    cursor: busy ? 'default' : 'pointer',
    background: tone === 'solid' ? P.ink : 'transparent',
    color: tone === 'solid' ? P.sheet : P.muted,
    border: `0.5px solid ${tone === 'solid' ? P.ink : P.rule}`,
    opacity: busy ? 0.5 : 1, whiteSpace: 'nowrap',
  });

  const detail = error ? describePrepressError(error.body) : null;
  // §6's own check, reused: a blank still carrying its unflapped corner count
  // means the flaps were never added, however healthy the response looked.
  // Tri-state — null is "this deployment gave us nothing to check against",
  // which must not be reported as a failure.
  const gained = described ? blankHasFlaps(described.entry, caps) : null;
  const blankVerts = described?.entry?.assets?.blank?.vertices_mm || null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(20,16,12,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 540, background: P.sheet, border: `0.5px solid ${P.rule}`,
        borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(20,16,12,.28)',
      }}>
        <div style={{ padding: '16px 18px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.warn }}>
              No flaps yet
            </div>
            {remaining > 0 && (
              <div style={{ fontFamily: MO, fontSize: 10, color: P.dim }}>{remaining} more to check</div>
            )}
          </div>
          <div style={{ fontFamily: DISPLAY, fontSize: 23, color: P.ink, lineHeight: 1.15, marginTop: 2 }}>
            {sourceId}
          </div>
        </div>

        <div style={{ padding: '0 18px 16px' }}>
          <p style={{ fontFamily: FN, fontSize: 12.5, color: P.muted, margin: '0 0 14px', lineHeight: 1.55 }}>
            This outline has {corners} corners — an unflapped carton base, not a finished
            dieline. Placed as it is, it goes on the sheet without its glue, tuck and dust
            flaps, and nests against its neighbours by the wrong shape.
            {' '}Prepress adds the flaps and hands back the real blank.
          </p>

          {/* The family. Pre-chosen when only one takes this count; asked when
              more than one does, because a vertex count does not name a family
              and an 8-corner STE guessed as BL is cut as the wrong carton. */}
          {suggested.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: MO, fontSize: 9.5, textTransform: 'uppercase', letterSpacing: 0.5, color: P.dim, marginBottom: 6 }}>
                Carton type
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {suggested.map((t) => (
                  <button key={t} onClick={() => onCartonType(t)} disabled={busy}
                    title={`Ask prepress to build this as a ${t} carton`}
                    style={{
                      fontFamily: MO, fontSize: 11, padding: '5px 12px', borderRadius: 5,
                      cursor: busy ? 'default' : 'pointer',
                      background: cartonType === t ? P.ink : 'transparent',
                      color: cartonType === t ? P.sheet : P.muted,
                      border: `0.5px solid ${cartonType === t ? P.ink : P.rule}`,
                    }}>{t}</button>
                ))}
              </div>
              {needsChoice && (
                <div style={{ fontFamily: FN, fontSize: 10.5, color: P.dim, marginTop: 6, lineHeight: 1.45 }}>
                  {suggested.join(' and ')} both take {corners} corners on this server. The corner
                  count cannot tell them apart — pick the one this carton is.
                </div>
              )}
            </div>
          )}

          {/* No family takes this count. Prepress cannot be asked at all, so the
              only honest offer left is to place the outline as drawn. */}
          {suggested.length === 0 && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, marginBottom: 14,
              background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
              fontFamily: FN, fontSize: 11.5, color: P.ink, lineHeight: 1.5,
            }}>
              No carton type this server offers takes a {corners}-corner base, so prepress
              cannot build the flaps for it. If the flaps are already drawn, place it as
              drawn; otherwise it needs re-exporting from the die shop.
            </div>
          )}

          {/* What came back. Shown before it is accepted — the operator is
              about to place this die and should see it first. */}
          {described && (
            <div style={{
              display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14,
              padding: '10px 12px', borderRadius: 6,
              background: gained === false ? P.dangerSoft : P.okSoft,
              border: `0.5px solid ${gained === false ? P.dangerLine : P.okLine}`,
            }}>
              {/* THE POLYGON THAT WILL BE PLACED, not a projection of it.
                  `preview.after` is the same geometry, but it arrives from
                  describe under `vertices_mm` while `prepressPreviewFor` renames
                  it — reading the blank we are actually about to commit removes
                  the chance of showing one die and placing another. */}
              {Array.isArray(blankVerts) && blankVerts.length >= 3 && (
                <DiePreview vertices={blankVerts} size={64} />
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: MO, fontSize: 11, color: P.ink }}>
                  {gained === false ? 'Prepress returned an unflapped blank' : 'Flaps added'}
                </div>
                <div style={{ fontFamily: MO, fontSize: 10, color: P.muted, marginTop: 2 }}>
                  {blankSummaryText(described.entry) || 'blank ready'}
                </div>
                {gained === false && (
                  <div style={{ fontFamily: FN, fontSize: 10.5, color: P.ink, marginTop: 4, lineHeight: 1.45 }}>
                    Its corner count has not changed, so the flaps were not added. Do not
                    place this — take it back to the die shop.
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 6, marginBottom: 14,
              background: P.dangerSoft, border: `0.5px solid ${P.dangerLine}`,
              fontFamily: FN, fontSize: 11.5, color: P.ink, lineHeight: 1.5, whiteSpace: 'pre-line',
            }}>
              {detail ? `${detail.title}${detail.hint ? `\n${detail.hint}` : ''}` : (error.message || String(error))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {described
              ? (
                <button onClick={() => onAddFlaps(cartonType, described)} disabled={busy || gained === false}
                  style={{ ...btn('solid'), opacity: (busy || gained === false) ? 0.4 : 1 }}>
                  Put it on the desk
                </button>
              )
              : (
                <button onClick={() => onAddFlaps(cartonType, null)} disabled={busy || !canAsk}
                  title={canAsk ? 'Ask prepress to build this carton’s blank' : 'Pick a carton type first'}
                  style={{ ...btn('solid'), opacity: (busy || !canAsk) ? 0.4 : 1 }}>
                  {busy ? 'Asking prepress…' : 'Add flaps with prepress'}
                </button>
              )}
            {/* Never removed, never hidden. The check is an affordance and it is
                wrong in both directions often enough that the operator has to
                be able to overrule it in one click. */}
            <button onClick={onPlaceAsDrawn} disabled={busy} style={btn()}
              title="Place this outline exactly as it was detected, with no flaps added">
              Place as drawn
            </button>
            <button onClick={onCancel} disabled={busy} style={{ ...btn(), marginLeft: 'auto', color: P.dim }}
              title="Do not put this carton on the desk at all">
              Skip this carton
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
